import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

/**
 * Lightweight autocomplete for the record/request `site` field.
 *
 * Behaviour:
 *   • Fetches `/api/sites/suggest` as the user types (300ms debounce).
 *   • When `customerId` is set, sites previously used for that customer float
 *     to the top (returned by the backend already).
 *   • Users can still type a brand-new site — we don't force selection.
 *   • Dropdown closes on outside-click or Escape.
 */
export default function SiteAutocomplete({
  value,
  onChange,
  customerId,
  placeholder = "Site",
  className = "",
  disabled = false,
  "data-testid": testId = "site-autocomplete",
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced fetch — fires 300ms after the last keystroke OR on first focus.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        abortRef.current?.abort?.();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const params = { limit: 10 };
        if (value && value.trim()) params.q = value.trim();
        if (customerId && customerId !== "__none__") params.customer_id = customerId;
        const res = await api.get("/sites/suggest", { params, signal: ctrl.signal });
        setOptions(res.data?.sites || []);
        setHighlight(-1);
      } catch {
        /* ignore — new entry can proceed without suggestions */
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value, open, customerId]);

  // Outside-click close.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(options.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(options[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="rounded-none border-neutral-400"
        data-testid={testId}
      />
      {open && options.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto border border-neutral-900 bg-white shadow-md"
          data-testid={`${testId}-menu`}
        >
          {options.map((opt, i) => (
            <button
              type="button"
              key={opt}
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-neutral-100 ${
                highlight === i ? "bg-neutral-100" : ""
              }`}
              data-testid={`${testId}-opt-${i}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
